import React, { useEffect, useRef } from "react";
import {
  ArrowRight, Sparkles, Youtube, Instagram, Send, Crown, TrendingUp,
  Shield, Brain, Target, Quote, PlayCircle, Users, Rocket,
} from "lucide-react";

const SOCIALS = [
  {
    key: "youtube", label: "YouTube", handle: "@wealthsecret23",
    href: "https://www.youtube.com/@wealthsecret23", Icon: Youtube,
    accent: "linear-gradient(135deg,#ff0000,#c81d25)", sub: "1M+ traders",
  },
  {
    key: "instagram", label: "Instagram", handle: "@wealthsecretofficial",
    href: "https://www.instagram.com/wealthsecretofficial/", Icon: Instagram,
    accent: "linear-gradient(135deg,#f58529,#dd2a7b,#8134af)", sub: "Daily insights",
  },
  {
    key: "telegram", label: "Telegram", handle: "wealthsecretofficial1",
    href: "https://t.me/wealthsecretofficial1", Icon: Send,
    accent: "linear-gradient(135deg,#229ED9,#2AABEE)", sub: "Live community",
  },
];

const TIMELINE = [
  {
    year: "The Start", Icon: Rocket, title: "A conventional beginning",
    desc: "Rooted in Maharashtra, Sagar started the way most do — a corporate role at Havells India while building an electronics retail business on the side. Ambition, but not yet freedom.",
  },
  {
    year: "2022", Icon: Shield, title: "The setback that changed everything",
    desc: "The pandemic forced his electronics business to shut down. Instead of stepping back, he went all-in on the markets — self-taught, endless screen-time, and every hard lesson the market charges for.",
  },
  {
    year: "Sept 2022", Icon: PlayCircle, title: "Wealth Secret is born",
    desc: "He launched the Wealth Secret Official YouTube channel to teach exactly what he learned the hard way — stock markets, crypto, technical analysis and, above all, trading psychology.",
  },
  {
    year: "Today", Icon: Users, title: "A seven-figure family",
    desc: "The channel has grown past a million traders and Wealth Secret is now a full ecosystem — structured courses, live market reads and a private VIP community that trades together.",
  },
];

const VALUES = [
  { Icon: Shield, title: "Capital first", desc: "Protect the downside and the profits follow. Risk management is the strategy — not an afterthought." },
  { Icon: Brain, title: "Master your mind", desc: "Emotional discipline beats any indicator. The edge is built between the ears, one honest trade at a time." },
  { Icon: Target, title: "Process over profit", desc: "Chase the right decisions, not quick money. Consistency compounds; gambling doesn't." },
  { Icon: TrendingUp, title: "Teach what's real", desc: "No hype, no fake screenshots — only lessons forged from real trades and real losses turned into wisdom." },
];

const useReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const els = root.querySelectorAll(".ws-reveal");
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add("ws-in"); io.unobserve(e.target); } });
    }, { threshold: 0.14 });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
  return ref;
};

const AboutUs = ({ onGetStarted }) => {
  const ref = useReveal();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="ws-landing ws-about" ref={ref} data-testid="about-page">
      {/* Top bar */}
      <nav className="ws-nav-wrap" data-testid="about-nav">
        <div className="ws-nav-pill">
          <a href="/" className="ws-nav-brand" data-testid="about-nav-home">
            <span className="ws-logo-3d w-9 h-9"><img src="/wsj-logo.webp" alt="Wealth Secret Journal" /></span>
            <span className="leading-none">
              <span className="block text-white font-extrabold text-[15px] tracking-tight">Wealth Secret</span>
              <span className="block text-[9px] tracking-[0.28em] text-[color:var(--ws-muted)] uppercase">Journal</span>
            </span>
          </a>
          <div className="ws-nav-links">
            <a href="/" className="ws-nav-link" data-testid="about-link-home">Home</a>
            <a href="/#features" className="ws-nav-link">Features</a>
            <a href="/#ai-coach" className="ws-nav-link">WS Coach</a>
            <a href="/contact" className="ws-nav-link">Contact</a>
          </div>
          <div className="ws-nav-actions">
            <button onClick={onGetStarted} className="ws-btn-3d ws-nav-cta" data-testid="about-getstarted">
              <Sparkles className="w-4 h-4" /> Start Free
            </button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <header className="ws-about-hero">
        <div className="ws-about-aurora" aria-hidden />
        <div className="ws-about-hero-inner">
          <div className="ws-about-hero-copy ws-reveal">
            <span className="ws-about-eyebrow"><Crown className="w-3.5 h-3.5" /> The Founder's Story</span>
            <h1 className="ws-about-title">
              Meet <span className="ws-brand-name ws-crown-word">Sagar Naik</span>
            </h1>
            <p className="ws-about-lead">
              Founder of Wealth Secret. Self-taught trader, relentless teacher, and the mind behind a
              community of over a million traders — built from a comeback, not a shortcut.
            </p>
            <div className="ws-about-hero-stats">
              <div className="ws-about-stat"><b>1M+</b><span>YouTube family</span></div>
              <div className="ws-about-stat"><b>2022</b><span>All-in on markets</span></div>
              <div className="ws-about-stat"><b>100%</b><span>Self-taught</span></div>
            </div>
            <div className="ws-about-hero-actions">
              <a href="https://www.youtube.com/@wealthsecret23" target="_blank" rel="noopener noreferrer"
                 className="ws-btn-primary px-6 py-3 rounded-xl text-[14px] font-semibold inline-flex items-center gap-2" data-testid="about-hero-youtube">
                <Youtube className="w-4 h-4" /> Watch the journey
              </a>
              <button onClick={onGetStarted} className="ws-btn-3d px-6 py-3 rounded-xl text-[14px] font-semibold inline-flex items-center gap-2" data-testid="about-hero-cta">
                Start your edge <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="ws-about-hero-media ws-reveal">
            <div className="ws-about-portrait ws-about-tilt">
              <img src="/founder/founder-studio-lean.webp" alt="Sagar Naik, Founder of Wealth Secret" loading="eager" />
              <div className="ws-about-portrait-glow" aria-hidden />
              <div className="ws-about-badge ws-about-badge-a"><Crown className="w-4 h-4" /> Founder & CEO</div>
              <div className="ws-about-badge ws-about-badge-b"><Users className="w-4 h-4" /> 1M+ community</div>
            </div>
          </div>
        </div>
      </header>

      {/* Quote band */}
      <section className="ws-about-quote ws-reveal">
        <Quote className="ws-about-quote-mark" />
        <p>“I lost a business, not my belief. The market gave me a second life — now I hand the map to everyone who's willing to do the work.”</p>
        <span className="ws-about-quote-by">— Sagar Naik</span>
      </section>

      {/* Founder gallery */}
      <section className="ws-about-gallery-sec">
        <div className="ws-about-gallery">
          <div className="ws-about-photo ws-about-tilt ws-reveal">
            <img src="/founder/founder-studio-fist.webp" alt="Sagar Naik" loading="lazy" />
          </div>
          <div className="ws-about-photo ws-about-tilt ws-reveal" style={{ transitionDelay: "120ms" }}>
            <img src="/founder/founder-laptop.webp" alt="Sagar Naik trading" loading="lazy" />
          </div>
        </div>
      </section>

      {/* Journey timeline */}
      <section className="ws-about-journey">
        <div className="ws-about-section-head ws-reveal">
          <span className="ws-about-eyebrow"><Rocket className="w-3.5 h-3.5" /> The Journey</span>
          <h2 className="ws-about-h2">From a shut-down shop to a trading movement</h2>
        </div>
        <div className="ws-about-journey-grid">
          <div className="ws-about-timeline">
            {TIMELINE.map((t, i) => (
              <div key={i} className="ws-about-tl-item ws-reveal" style={{ transitionDelay: `${i * 80}ms` }}>
                <div className="ws-about-tl-dot"><t.Icon className="w-4 h-4" /></div>
                <div className="ws-about-tl-body">
                  <span className="ws-about-tl-year">{t.year}</span>
                  <h3 className="ws-about-tl-title">{t.title}</h3>
                  <p className="ws-about-tl-desc">{t.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="ws-about-journey-media">
            <div className="ws-about-photo ws-about-tilt ws-reveal">
              <img src="/founder/founder-boat.webp" alt="Sagar Naik at sunset" loading="lazy" />
            </div>
            <div className="ws-about-photo ws-about-tilt ws-reveal" style={{ transitionDelay: "120ms" }}>
              <img src="/founder/founder-aframe.webp" alt="Sagar Naik" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="ws-about-values">
        <div className="ws-about-section-head ws-reveal">
          <span className="ws-about-eyebrow"><Brain className="w-3.5 h-3.5" /> What he stands for</span>
          <h2 className="ws-about-h2">The philosophy behind Wealth Secret</h2>
        </div>
        <div className="ws-about-values-grid">
          {VALUES.map((v, i) => (
            <div key={i} className="ws-about-value ws-reveal" style={{ transitionDelay: `${i * 70}ms` }}>
              <div className="ws-about-value-ic"><v.Icon className="w-5 h-5" /></div>
              <h3 className="ws-about-value-title">{v.title}</h3>
              <p className="ws-about-value-desc">{v.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Socials */}
      <section className="ws-about-social">
        <div className="ws-about-section-head ws-reveal">
          <span className="ws-about-eyebrow"><Sparkles className="w-3.5 h-3.5" /> Join the movement</span>
          <h2 className="ws-about-h2">Follow Sagar &amp; the Wealth Secret family</h2>
        </div>
        <div className="ws-about-social-grid">
          {SOCIALS.map(({ key, label, handle, href, Icon, accent, sub }) => (
            <a key={key} href={href} target="_blank" rel="noopener noreferrer"
               className="ws-about-social-card ws-reveal" data-testid={`about-social-${key}`}>
              <span className="ws-about-social-ic" style={{ background: accent }}><Icon className="w-6 h-6" /></span>
              <span className="ws-about-social-meta">
                <span className="ws-about-social-label">{label}</span>
                <span className="ws-about-social-handle">{handle}</span>
                <span className="ws-about-social-sub">{sub}</span>
              </span>
              <ArrowRight className="ws-about-social-arrow w-4 h-4" />
            </a>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="ws-about-final ws-reveal">
        <div className="ws-about-final-inner">
          <span className="ws-hero-logo ws-logo-3d"><img src="/wsj-logo.webp" alt="Wealth Secret" /></span>
          <h2 className="ws-about-h2">Trade with the edge Sagar built</h2>
          <p className="ws-about-lead ws-about-final-lead">
            Auto-sync your broker, let AI find your patterns, and turn raw trades into a measurable edge —
            the same discipline that powers the Wealth Secret community.
          </p>
          <button onClick={onGetStarted} className="ws-btn-primary px-7 py-3.5 rounded-xl text-[15px] font-semibold inline-flex items-center gap-2 mt-2" data-testid="about-final-cta">
            <Sparkles className="w-4 h-4" /> Start Free
          </button>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
